/**
 * Sesión actual del Admin: `GET /api/v1/admin/me` bajo la clave de query `['me']`
 * (implementation-notes §4). Todo el shell lee de aquí: permisos, empresa, modo soporte y avisos.
 */
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { MeResponseSchema, type MeResponse, type Permission } from '@fermin/contracts';

import { api, type ApiError } from '../../api/client';

export const ME_QUERY_KEY = ['me'] as const;

export function fetchMe(): Promise<MeResponse> {
  return api.get('/me', MeResponseSchema);
}

export function useMe(): UseQueryResult<MeResponse, ApiError> {
  return useQuery<MeResponse, ApiError>({
    queryKey: ME_QUERY_KEY,
    queryFn: fetchMe,
  });
}

/** `true` si `me.permissions` incluye el permiso. La API siempre revalida. */
export function hasPermission(me: MeResponse | undefined, permission: Permission): boolean {
  return me?.permissions.includes(permission) ?? false;
}

/** La empresa está suspendida: la UI pasa a solo lectura y muestra el banner. */
export function isReadOnly(me: MeResponse | undefined): boolean {
  return me?.company?.status === 'SUSPENDED';
}

/** `POST /auth/logout`: revoca la sesión actual y limpia la caché de queries. */
export function useLogout(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post('/auth/logout'),
    onSuccess: () => {
      queryClient.clear();
      onSuccess?.();
    },
  });
}

/** `DELETE /support-session`: sale del modo soporte y devuelve una sesión de panel. */
export function useExitSupport(onSuccess?: () => void) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.delete('/support-session'),
    onSuccess: async () => {
      await queryClient.invalidateQueries();
      onSuccess?.();
    },
  });
}
